import { CommonModule, DatePipe } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { inject, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import {
  NgxGoogleAnalyticsModule,
  NgxGoogleAnalyticsRouterModule,
} from 'ngx-google-analytics';
import { MomentModule } from 'ngx-moment';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxPermissionsModule } from 'ngx-permissions';

// components
import { MatDividerModule } from '@angular/material/divider';
import { HeaderService } from 'src/app/core/services/header';
import { TicketService } from 'src/app/core/services/ticket';
import { appReducer, effects } from 'src/app/store';
import { AppLayoutComponent } from './_layout/app-layout/app-layout.component';
import { PublicLayoutComponent } from './_layout/public-layout/public-layout.component';
import { AppComponent } from './app.component';
import { PageNotFoundComponent } from './core/page-not-found/page-not-found.component';
import { NocPolicyComponent } from './noc-policy/noc-policy.component';
import { FooterComponent } from './shared/portal-layout/footer/footer.component';

// modules
import { AppRoutingModule } from './app-routing.module';
import { BillingModule } from './billing/billing.module';
import { GainsightModule } from './core/gainsight.module';
import { InventoryModule } from './inventory/inventory.module';
import { ManageUsersModule } from './manage-users/manage-users.module';
import { OrdersModule } from './orders/orders.module';
import { SharedModule } from './shared/shared.module';
import { TicketModule } from './tickets/ticket.module';

// analytics modules
import { IqAggregatedDashboardModule } from './iq-analytics/aggregated-dashboard/aggregated-dashboard.module';
import { MetricDetailsModule } from './iq-analytics/metric-details/metric-details.module';
import { QualityOfExperienceModule } from './iq-analytics/quality-of-experience/quality-of-experience.module';
import { UnusualNetworkMetricsModule } from './iq-analytics/unusual-network-metrics/unusual-network-metrics.module';
import { OverallNetworkStatusModule } from './overall-network-status/overall-network-status.module';

// services
import { CSVFormatterService } from './core/services/csv-formatter/csv-formatter.service';
import { StateService } from './core/services/state/state.service';

import { environment } from '../environments/environment';
import { gainsightTagKey } from './app.config';
import { initializeAppProviders } from './app.providers';
import { NonceService } from './core/services/nonce/nonce.service';
import { TokenInterceptor } from './core/token.interceptor';
import { SeverityLevelContactsGridComponent } from './noc-policy/components/severity-level-contacts-grid/severity-level-contacts-grid.component';
import { clearState } from './store/reducers/logout.reducer';

@NgModule({
  declarations: [
    AppComponent,
    AppLayoutComponent,
    PageNotFoundComponent,
    PublicLayoutComponent,
    NocPolicyComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    CommonModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    SharedModule,
    TicketModule,
    BillingModule,
    InventoryModule,
    OrdersModule,
    ManageUsersModule,
    MomentModule,
    MatSortModule,
    MatDividerModule,
    MatTableModule,
    NgxPaginationModule,
    MetricDetailsModule,
    OverallNetworkStatusModule,
    NgIdleKeepaliveModule.forRoot(),
    NgxPermissionsModule.forRoot(),
    StoreModule.forRoot(appReducer, {
      metaReducers: [clearState],
    }),
    EffectsModule.forRoot(effects),
    StoreRouterConnectingModule.forRoot(),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    NgxGoogleAnalyticsModule,
    NgxGoogleAnalyticsRouterModule,
    GainsightModule.forRoot({ tagKey: gainsightTagKey }),
    IqAggregatedDashboardModule,
    UnusualNetworkMetricsModule,
    QualityOfExperienceModule,
    MatIconModule,
    SeverityLevelContactsGridComponent,
    FooterComponent,
  ],
  providers: [
    initializeAppProviders,
    DatePipe,
HeaderService,
    TicketService,
    StateService,
    CSVFormatterService,
    TokenInterceptor,
    NonceService,
    provideHttpClient(withInterceptorsFromDi()),
    provideCharts(withDefaultRegisterables()),
  ],
})
export class AppModule {
  private matIconRegistry = inject(MatIconRegistry);

  private sanitizer = inject(DomSanitizer);

  constructor() {
    this.matIconRegistry.addSvgIcon(
      'billing',
      this.sanitizer.bypassSecurityTrustResourceUrl(
        '/assets/images/icons_legacy/billing_lrg.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'pdf-file',
      this.sanitizer.bypassSecurityTrustResourceUrl(
        '/assets/images/icons_legacy/pdf.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'question-mark',
      this.sanitizer.bypassSecurityTrustResourceUrl(
        '/assets/images/icons_legacy/help.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      `csv_file`,
      this.sanitizer.bypassSecurityTrustResourceUrl(
        `/assets/images/icons_legacy/csv-icon.svg`,
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'user-cb',
      this.sanitizer.bypassSecurityTrustResourceUrl(
        '/assets/images/icons/user-cb.svg',
      ),
    );
  }

}
